import React, { useRef, useEffect, useState, useId } from 'react';
import * as d3 from 'd3';
import { quantile } from 'd3-array';

/* 
{
    min: min of the dataset
    q1: 25th percentile,
    median: the median of the dataset,
    q3: the 75th percentile,
    max: the max of the dataset,
    iqr: q3-q1,
    lowerWhisker: max(min of dataset, q1 - (1.5 * iqr) ),
    upperWhisker: min(max of dataset, q1 + (1.5 * iqr) )
} 
*/

export function summaryStats(data) {
    if (!data || data.length === 0) {
        return null;
    }

    // Filter out non-numeric values
    const values = data.map(v => v === '' ? NaN : Number(v)).filter(v => !isNaN(v) && isFinite(v));
    if (values.length === 0) {
        return null;
    }

    // Sort the data
    const sorted = values.sort((a, b) => a - b);
    
    // Calculate basic statistics
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const q1 = quantile(sorted, 0.25);
    const median = quantile(sorted, 0.5);
    const q3 = quantile(sorted, 0.75);
    const iqr = q3 - q1;
    const n = sorted.length;
    
    // Calculate whiskers using 1.5 * IQR rule
    const lowerWhisker = Math.max(min, q1 - (1.5 * iqr));
    const upperWhisker = Math.min(max, q3 + (1.5 * iqr));
    
    return {
        min,
        q1,
        median,
        q3,
        max,
        iqr,
        lowerWhisker,
        upperWhisker,
        n
    };
}


/*
data should look like
{
    // e.g. selected BMP
    group1: [{...}, {...}, ...],

    // e.g. Comparison data (The other BMPs of the same type)
    group2: [{...}, {...}, ...]
}
*/
export default function JitterBoxPlot({
    data = {},
    accessors = [],
    legendLabels = [],
    width,
    height = 300,
    margin = { top: 20, right: 20, bottom: 60, left: 60 },
    yLabel = "",
    xLabel = "",
    referenceLine = null,
    colors = [] // typically you want to set this to correspond to the number of accessors
}) {
    const svgRef = useRef();
    const containerRef = useRef();
    const [plotSize, setPlotSize] = useState({ width: 400, height: 300 });
    const [zoomDomains, setZoomDomains] = useState(null); // { x: [min, max], y: [min, max] } or null
    // Tooltip state: { visible, x, y, content }
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

    // Responsive sizing
    useEffect(() => {
        function handleResize() {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const newWidth = width || Math.max(300, Math.min(containerWidth - 20, 600));
                const newHeight = height || Math.max(250, Math.min(newWidth * 0.75, 400));
                setPlotSize({ width: newWidth, height: newHeight });
            }
        }

        handleResize();
        window.addEventListener('resize', handleResize);

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setTimeout(handleResize, 0);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            observer.disconnect();
        };
    }, [width, height]);
    // --- CLIPPING LOGIC ---
    // Use a unique id for the SVG clipPath to avoid id collisions if multiple plots are rendered on the same page.
    // React 18+ provides useId for this purpose; fallback to a random string if unavailable.
    const clipPathId = useId ? useId() : `plot-clip-${Math.random().toString(36).slice(2, 10)}`;


    // Always derive groupList from the current data keys to ensure it stays in sync with the data prop.
    // This prevents bugs where groupList and data could get out of sync, especially when toggling comparison mode.
    const groupList = Object.keys(data);
    const accessorList = accessors;

    // When groupList or accessorList changes, check if the current zoomDomains.x is still valid.
    // If the zoom x-domain does not match the new groupList, reset zoomDomains to prevent x-axis misalignment and errors.
    useEffect(() => {
        if (
            zoomDomains &&
            zoomDomains.x &&
            (
                zoomDomains.x.length !== groupList.length ||
                !zoomDomains.x.every((g, i) => g === groupList[i])
            )
        ) {
            setZoomDomains(null);
        }
    }, [JSON.stringify(groupList), JSON.stringify(accessorList)]);

    // Compute all points for x and y domain calculations
    // Guard against undefined data[group] by defaulting to an empty array.
    // This prevents errors if groupList contains a group that is not present in data (e.g., after toggling comparison mode).
    const plotItems = [];
    groupList.forEach((group, groupIndex) => {
        accessorList.forEach((accessor, accessorIndex) => {
            const points = (data[group] || []).filter(v => !isNaN(v[accessor]) && isFinite(v[accessor]) && (v[accessor] !== '') );
            const summaryData = summaryStats(points.map(p => p[accessor])); // Ensure we have valid points
            if (points.length > 0) {
                plotItems.push({
                    group,
                    accessor,
                    points,
                    numericPoints: points.map(p => p[accessor]),
                    summaryData,
                    color: colors[(groupIndex * accessorList.length + accessorIndex) % colors.length]
                });
            }
        });
    });

    console.log("plotItems", plotItems)
    
    // X domain: all group-accessor combos
    const xDomain = groupList;
    // Y domain: all points
    const allPoints = plotItems.flatMap(d => d.numericPoints);
    const yDomain = d3.extent(allPoints);

    // Only use zoomDomains.x if it matches the current groupList exactly.
    // This prevents using a stale or mismatched x-domain, which can cause x-axis misalignment after toggling comparison mode or changing data.
    const isValidZoomX = zoomDomains && zoomDomains.x &&
        zoomDomains.x.length === groupList.length &&
        zoomDomains.x.every((g, i) => g === groupList[i]);
    const xScaleDomain = isValidZoomX ? zoomDomains.x : xDomain;
    const yScaleDomain = zoomDomains && zoomDomains.y ? zoomDomains.y : yDomain;

    useEffect(() => {
        if (!data || Object.keys(data).length === 0) return;
        const svg = d3.select(svgRef.current);
        const innerWidth = plotSize.width - margin.left - margin.right;
        const innerHeight = plotSize.height - margin.top - margin.bottom;

        // --- SVG CLIP PATH DEFINITION ---
        // Always ensure <defs> is present and before the plot group, and that the clipPath id is unique.
        // This prevents SVG id collisions and ensures robust clipping, especially with multiple plots.
        let defs = svg.select('defs');
        if (defs.empty()) {
            defs = svg.insert('defs', ':first-child');
            defs.append('clipPath')
                .attr('id', clipPathId)
                .append('rect')
                .attr('x', 0)
                .attr('y', 0)
                .attr('width', innerWidth)
                .attr('height', innerHeight);
        } else {
            defs.select('clipPath rect')
                .attr('width', innerWidth)
                .attr('height', innerHeight);
            defs.select('clipPath')
                .attr('id', clipPathId);
        }

        // X scale: band for groups
        const xScale = d3.scaleBand()
            .domain(xScaleDomain)
            .range([0, innerWidth])
            .padding(0.3);
        // Y scale: linear
        const yScale = d3.scaleLinear()
            .domain(yScaleDomain)
            .range([innerHeight, 0])
            .nice();

        // Main group (for axes)
        let g = svg.select('g.main-group');
        if (g.empty()) {
            g = svg.append('g').attr('class', 'main-group')
                .attr('transform', `translate(${margin.left}, ${margin.top})`);
        } else {
            g.attr('transform', `translate(${margin.left}, ${margin.top})`);
        }

        // Axes
        let xAxisG = g.select('g.x-axis');
        if (xAxisG.empty()) {
            xAxisG = g.append('g').attr('class', 'x-axis')
                .attr('transform', `translate(0, ${innerHeight})`);
        } else {
            xAxisG.attr('transform', `translate(0, ${innerHeight})`);
        }
        xAxisG.transition().duration(1000).call(d3.axisBottom(xScale));

        let yAxisG = g.select('g.y-axis');
        if (yAxisG.empty()) {
            yAxisG = g.append('g').attr('class', 'y-axis');
        }
        yAxisG.transition().duration(1000).call(d3.axisLeft(yScale).tickFormat(d => {
            if (Math.abs(d) >= 1e6) return d / 1e6 + 'M';
            if (Math.abs(d) >= 1e3) return d / 1e3 + 'K';
            return d;
        }));

        // Axis labels (static)
        let xLabelGroup = g.select('text.x-label');
        if (xLabelGroup.empty()) {
            xLabelGroup = g.append('text').attr('class', 'x-label')
                .attr('x', innerWidth / 2)
                .attr('y', innerHeight + margin.bottom - 10)
                .attr('text-anchor', 'middle')
                .style('font-size', '12px')
                .text(xLabel);
        } else {
            xLabelGroup.attr('x', innerWidth / 2).attr('y', innerHeight + margin.bottom - 10);
        }
        let yLabelText = g.select('text.y-label');
        if (yLabelText.empty()) {
            yLabelText = g.append('text').attr('class', 'y-label')
                .attr('transform', 'rotate(-90)')
                .attr('x', -innerHeight / 2)
                .attr('y', -margin.left + 20)
                .attr('text-anchor', 'middle')
                .style('font-size', '12px')
                .text(yLabel);
        } else {
            yLabelText.attr('x', -innerHeight / 2).attr('y', -margin.left + 20).text(yLabel);
        }

        // Reference line
        let refLine = g.select('line.reference-line');
        if (referenceLine !== null && !isNaN(referenceLine)) {
            if (refLine.empty()) {
                refLine = g.append('line').attr('class', 'reference-line');
            }
            refLine.transition().duration(1000)
                .attr('x1', 0)
                .attr('x2', innerWidth)
                .attr('y1', yScale(referenceLine))
                .attr('y2', yScale(referenceLine))
                .attr('stroke', '#e53e3e')
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '5,5')
                .attr('opacity', 0.7);
        } else {
            refLine.remove();
        }

        // --- PLOTTING GROUP WITH CLIPPING ---
        // All plot elements (boxes, whiskers, points) are appended to this group, which is clipped to the plot area.
        let plotG = g.select('g.plot-group');
        if (plotG.empty()) {
            plotG = g.append('g').attr('class', 'plot-group')
                .attr('clip-path', `url(#${clipPathId})`);
        } else {
            plotG.attr('clip-path', `url(#${clipPathId})`);
        }

        // --- DATA JOIN FOR BOXPLOTS ---
        // For each plotItem (group-accessor combo)
        // We need to flatten the data for D3 joins
        const boxData = plotItems.map((d, i) => {
            const boxWidth = xScale.bandwidth() / accessorList.length;
            const x = xScale(d.group);
            const accessorIndex = accessorList.indexOf(d.accessor);
            const boxX = x + accessorIndex * boxWidth;
            return {
                ...d,
                boxWidth,
                boxX,
                key: `${d.group}-${d.accessor}`
            };
        });

        // Whiskers
        const whiskers = plotG.selectAll('line.whisker')
            .data(boxData, d => d.key);
        whiskers.enter()
            .append('line')
            .attr('class', 'whisker')
            .attr('x1', d => d.boxX + d.boxWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2)
            .attr('y1', d => yScale(d.summaryData.lowerWhisker))
            .attr('y2', d => yScale(d.summaryData.upperWhisker))
            .attr('stroke', d => d.color)
            .attr('stroke-width', 2)
            .merge(whiskers)
            .transition().duration(1000)
            .attr('x1', d => d.boxX + d.boxWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2)
            .attr('y1', d => yScale(d.summaryData.lowerWhisker))
            .attr('y2', d => yScale(d.summaryData.upperWhisker));
        whiskers.exit().remove();

        // Whisker caps (lower and upper)
        const capWidth = xScale.bandwidth() / accessorList.length * 0.3;
        const lowerCaps = plotG.selectAll('line.lower-cap')
            .data(boxData, d => d.key);
        lowerCaps.enter()
            .append('line')
            .attr('class', 'lower-cap')
            .attr('x1', d => d.boxX + d.boxWidth / 2 - capWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2 + capWidth / 2)
            .attr('y1', d => yScale(d.summaryData.lowerWhisker))
            .attr('y2', d => yScale(d.summaryData.lowerWhisker))
            .attr('stroke', d => d.color)
            .attr('stroke-width', 2)
            .merge(lowerCaps)
            .transition().duration(1000)
            .attr('x1', d => d.boxX + d.boxWidth / 2 - capWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2 + capWidth / 2)
            .attr('y1', d => yScale(d.summaryData.lowerWhisker))
            .attr('y2', d => yScale(d.summaryData.lowerWhisker));
        lowerCaps.exit().remove();

        const upperCaps = plotG.selectAll('line.upper-cap')
            .data(boxData, d => d.key);
        upperCaps.enter()
            .append('line')
            .attr('class', 'upper-cap')
            .attr('x1', d => d.boxX + d.boxWidth / 2 - capWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2 + capWidth / 2)
            .attr('y1', d => yScale(d.summaryData.upperWhisker))
            .attr('y2', d => yScale(d.summaryData.upperWhisker))
            .attr('stroke', d => d.color)
            .attr('stroke-width', 2)
            .merge(upperCaps)
            .transition().duration(1000)
            .attr('x1', d => d.boxX + d.boxWidth / 2 - capWidth / 2)
            .attr('x2', d => d.boxX + d.boxWidth / 2 + capWidth / 2)
            .attr('y1', d => yScale(d.summaryData.upperWhisker))
            .attr('y2', d => yScale(d.summaryData.upperWhisker));
        upperCaps.exit().remove();

        // Box rectangles
        const boxes = plotG.selectAll('rect.box')
            .data(boxData, d => d.key);
        boxes.enter()
            .append('rect')
            .attr('class', 'box')
            .attr('x', d => d.boxX)
            .attr('y', d => yScale(d.summaryData.q3))
            .attr('width', d => d.boxWidth)
            .attr('height', d => yScale(d.summaryData.q1) - yScale(d.summaryData.q3))
            .attr('fill', d => d.color)
            .attr('opacity', 0.3)
            .attr('stroke', d => d.color)
            .attr('stroke-width', 1)
            // Tooltip handlers for boxes
            .on('mouseenter', function(event, d) {
                const rect = containerRef.current.getBoundingClientRect();
                // Format numbers to max 2 decimal places, remove trailing zeros
                const fmt = n => (typeof n === 'number' ? n.toFixed(2).replace(/\.00$/, '').replace(/(\.[1-9]*)0+$/, '$1') : n);
                setTooltip({
                    visible: true,
                    x: event.clientX - rect.left + 10,
                    y: event.clientY - rect.top + 10,
                    content: `min: ${fmt(d.summaryData.min)}\nq1: ${fmt(d.summaryData.q1)}\nmedian: ${fmt(d.summaryData.median)}\nq3: ${fmt(d.summaryData.q3)}\nmax: ${fmt(d.summaryData.max)}\nn: ${fmt(d.summaryData.n)}`
                });
            })
            .on('mousemove', function(event) {
                const rect = containerRef.current.getBoundingClientRect();
                setTooltip(t => ({ ...t, x: event.clientX - rect.left + 10, y: event.clientY - rect.top + 10 }));
            })
            .on('mouseleave', function() {
                setTooltip(t => ({ ...t, visible: false }));
            })
            .merge(boxes)
            .transition().duration(1000)
            .attr('x', d => d.boxX)
            .attr('y', d => yScale(d.summaryData.q3))
            .attr('width', d => d.boxWidth)
            .attr('height', d => yScale(d.summaryData.q1) - yScale(d.summaryData.q3));
        boxes.exit().remove();

        // Median lines
        const medians = plotG.selectAll('line.median')
            .data(boxData, d => d.key);
        medians.enter()
            .append('line')
            .attr('class', 'median')
            .attr('x1', d => d.boxX)
            .attr('x2', d => d.boxX + d.boxWidth)
            .attr('y1', d => yScale(d.summaryData.median))
            .attr('y2', d => yScale(d.summaryData.median))
            .attr('stroke', d => d.color)
            .attr('stroke-width', 2)
            .merge(medians)
            .transition().duration(1000)
            .attr('x1', d => d.boxX)
            .attr('x2', d => d.boxX + d.boxWidth)
            .attr('y1', d => yScale(d.summaryData.median))
            .attr('y2', d => yScale(d.summaryData.median));
        medians.exit().remove();

        // --- DATA JOIN FOR POINTS ---
        // Flatten points for all group-accessor combos, with deterministic jitter
        const allPoints = boxData.flatMap((d, i) => d.points.map((p, j) => {
            const key = `${d.key}-${j}`;
            return {
                key,
                boxX: d.boxX,
                boxWidth: d.boxWidth,
                value: p[d.accessor],
                color: d.color,
                // Deterministic jitter based on key
                jitterX: d.boxX + d.boxWidth / 2 + (seededJitter(key) - 0.5) * d.boxWidth * 0.6
            };
        }));
        const points = plotG.selectAll('circle.point')
            .data(allPoints, d => d.key);
        points.enter()
            .append('circle')
            .attr('class', 'point')
            .attr('cx', d => d.jitterX)
            .attr('cy', d => yScale(d.value))
            .attr('r', 3)
            .attr('fill', d => d.color)
            .attr('opacity', 0.6)
            .attr('stroke', d => d.color)
            .attr('stroke-width', 1)
            // Tooltip handlers for dots
            .on('mouseenter', function(event, d) {
                const rect = containerRef.current.getBoundingClientRect();
                // Format value to max 3 decimal places, remove trailing zeros
                const fmt = n => (typeof n === 'number' ? n.toFixed(3).replace(/\.000$/, '').replace(/(\.[1-9]*)0+$/, '$1') : n);
                setTooltip({
                    visible: true,
                    x: event.clientX - rect.left + 10,
                    y: event.clientY - rect.top + 10,
                    content: `${fmt(d.value)}`
                });
            })
            .on('mousemove', function(event) {
                const rect = containerRef.current.getBoundingClientRect();
                setTooltip(t => ({ ...t, x: event.clientX - rect.left + 10, y: event.clientY - rect.top + 10 }));
            })
            .on('mouseleave', function() {
                setTooltip(t => ({ ...t, visible: false }));
            })
            .merge(points)
            .transition().duration(1000)
            .attr('cx', d => d.jitterX)
            .attr('cy', d => yScale(d.value));
        points.exit().remove();

        // --- BRUSH ---
        let brushG = g.select('g.brush');
        if (brushG.empty()) {
            // brushG = g.append('g').attr('class', 'brush');
            brushG = g.insert('g', ':first-child').attr('class', 'brush');
        }
        const brush = d3.brushY()
            .extent([[0, 0], [innerWidth, innerHeight]])
            .on('end', (event) => {
                if (!event.selection) return;
                const [y0, y1] = event.selection;
                const yStart = yScale.invert(y1);
                const yEnd = yScale.invert(y0);
                setZoomDomains({ x: xDomain, y: [yStart, yEnd] });
                brushG.call(brush.move, null);
            });
        brushG.call(brush);
    }, [data, plotSize, margin, yLabel, referenceLine, colors, accessors, zoomDomains]);

    const resetZoom = () => setZoomDomains(null);

    // Deterministic jitter function based on a string key
    // This keeps the dots in a static position when the boxplot renders, and prevents them from moving unnecessarily
    function seededJitter(key) {
        // Simple hash function to get a number from the key
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            hash = ((hash << 5) - hash) + key.charCodeAt(i);
            hash |= 0; // Convert to 32bit integer
        }
        // Use sine to get a pseudo-random value between 0 and 1
        return Math.abs(Math.sin(hash)) % 1;
    }

    // Legend logic
    // Dont show it if they didn't provide enough colors, otherwise its just going to be confusing
    let showLegend = true;
    let legendColors = colors;
    let legendLabelArray = legendLabels;

    // If no colors provided, generate random colors for each accessor
    if (!Array.isArray(colors) || colors.length === 0) {
        legendColors = Array.from({ length: accessorList.length }, () =>
            `hsl(${Math.floor(Math.random() * 360)},70%,55%)`
        );
    }

    // Only show legend if legendLabels is a valid array of the same length as accessors and not empty
    if (!Array.isArray(legendLabels) || legendLabels.length !== accessorList.length || legendLabels.length === 0) {
        showLegend = false;
    } else if (accessorList.length > legendColors.length) {
        console.warn(
            `[JitterBoxPlot] Not enough colors for accessors: got ${legendColors.length} colors for ${accessorList.length} accessors. Legend will be omitted.`
        );
        showLegend = false;
    } else {
        legendColors = legendColors.slice(0, accessorList.length);
        legendLabelArray = legendLabels.slice(0, accessorList.length);
    }

    return (
        <div ref={containerRef} className="w-full min-w-[300px] relative">
            {/* I am Legend */}
            {showLegend && (
                <div className="flex gap-4 items-center mb-2">
                    {accessorList.map((accessor, i) => (
                        <div key={accessor} className="flex items-center gap-1">
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    backgroundColor: legendColors[i],
                                    borderRadius: 100,
                                    border: '1px solid #ccc',
                                    marginRight: 4,
                                }}
                            />
                            <span style={{ fontSize: 13 }}>{legendLabelArray[i]}</span>
                        </div>
                    ))}
                </div>
            )}
            {/* END I am Legend */}

            <div className="flex justify-end items-center mb-2">
                {zoomDomains && (
                    <button 
                        onClick={resetZoom}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                        disabled={!zoomDomains}
                    >
                        Reset Zoom
                    </button>
                )}
            </div>
            {/* Tooltip overlay */}
            {tooltip.visible && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltip.x,
                        top: tooltip.y,
                        background: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: 4,
                        pointerEvents: 'none',
                        fontSize: 12,
                        zIndex: 10,
                        whiteSpace: 'pre'
                    }}
                >
                    {tooltip.content}
                </div>
            )}
            <svg
                ref={svgRef}
                width={plotSize.width}
                height={plotSize.height}
                className="w-full h-auto"
                style={{ maxWidth: '100%' }}
            />
        </div>
    );
} 